/**
 * The cheap view of a trip: everything a LIST needs, and nothing a list does
 * not. Pure — derived from a full trip, never hand-edited.
 *
 * ── Why a summary exists at all ───────────────────────────────────────────
 *
 * Firestore bills per DOCUMENT read, not per byte, so making trip documents
 * smaller saves nothing. What saves reads is not opening N documents to draw
 * a list of N rows. Every summary produced here is stored as one key inside a
 * single index document (`lib/trips.js`), so the whole list costs one read.
 *
 * ── Why it carries money, and why that is not optional ────────────────────
 *
 * The screens that scan every trip — dashboard totals, the pipeline board,
 * pending commission, `/insights` revenue, the search box — need numbers that
 * live deep inside `trip.data`: 20 `fin{n}amt` line items, 10 `pay{n}_amt`
 * rows with their `pay{n}_paid` dropdowns, 20 commission rows. If the summary
 * did not carry those totals, every one of those screens would have to fetch
 * every trip in full and the index would have bought nothing.
 *
 * So the totals are computed HERE, once, at write time, by the same
 * `lib/money.js` / `revenue.js` / `commission.js` functions the screens
 * already use. Two consequences worth stating plainly:
 *
 *   - A summary is only as right as the code that built it. When those
 *     modules change, `SUMMARY_VERSION` must go up and the index must be
 *     rebuilt, or the totals stay frozen at the old reading. That is what
 *     the version number is for.
 *   - `finTotal` is NOT the revenue figure. It is `readonly` in the markup
 *     and only two writers can put anything they like in it, one of which
 *     has put a budget band there in production (see `checkQuotedTotal` in
 *     `trip-shape.js`). `revenue` here is the line-item sum, matching
 *     `revenue.js`'s default; the quoted figure is carried separately and
 *     flagged when it is not a single number.
 */

import { sumMoney, isCancelled, isBooked, pipelineStage, tripPaid, tripScheduled, tripOutstanding } from './money.js'
import { tripRevenue, quotedTotal, revenueSource } from './revenue.js'
import { tripCommission, receivedCommission, commissionPaidMonth } from './commission.js'
import {
  TRIP_SCHEMA_VERSION,
  checkQuotedTotal,
  isArchivedTrip,
  mirrorTrip,
  tripCheckin,
  tripCheckout,
  tripClientName,
  tripDestination,
  tripSupplier,
} from './trip-shape.js'

/**
 * Bump whenever the SHAPE or the DERIVATION of a summary changes, including
 * when a money module it depends on changes its answer. An index built at a
 * lower version is rebuilt rather than trusted.
 */
/* 2 — added `email` and `phone` (16 Aug 2026). The Clients screen reconciles
   contact details OUT of trips and INTO client records, so a summary without
   them would have quietly stopped that backfill: no error, just client records
   that never gained an address. Measured before adding: 63 bytes per trip,
   46 KB at the 750-trip cap, 177 KB of headroom left under MAX_INDEX_BYTES.
   Bumping this invalidates every stored index, which is the point — they
   rebuild on next open. */
export const SUMMARY_VERSION = 2

/**
 * How many summaries the index will hold before it gives up on itself.
 *
 * MEASURED, not guessed (`tests/unit/trip-summary.test.js` re-measures it and
 * fails if it drifts): one filled summary is 764 bytes of JSON, and 992 with
 * long names — "Walt Disney World Resort, Lake Buena Vista, Orlando" is a
 * real destination. So 1,000 trips is 76% of Firestore's 1 MiB per-document
 * limit typically and 98% at the long end, which is not a margin.
 *
 * An index past the limit does not degrade, it FAILS THE WRITE — and the
 * write is batched with the trip save, so the advisor would lose the save
 * too. Hence a cap well under it, at which point the index reports
 * `overflowed`, every reader treats it as stale and falls back to reading the
 * collection, and the only thing lost is the read saving. That is the right
 * failure: slow, not wrong.
 *
 * The fix when an advisor really does reach 750 trips is to shard the index
 * (one document per year of `checkin`), not to raise this number.
 */
/* 700, down from 750 on 16 Aug 2026. Summary v2 added `email` and `phone`, and
   the wordiest 750-trip index then measured 791 KB against a 768 KB ceiling —
   caught by the test below, not by the estimate that preceded it, because a
   long address costs far more than the typical one it was estimated from.
   Past this cap the index stores nothing and every list falls back to reading
   the collection: expensive, correct, and reported. */
export const MAX_INDEXED_TRIPS = 700

/** The byte ceiling, checked alongside the count. 75% of Firestore's 1 MiB. */
export const MAX_INDEX_BYTES = 786432

/** @param {unknown} value @returns {string} */
function text(value) {
  return value == null ? '' : String(value).trim()
}

/**
 * One trip, as the lists see it.
 *
 * @param {any} trip - a full trip document
 * @returns {any} a flat object of scalars, safe to store in one Firestore map
 */
export function tripSummary(trip) {
  const t = mirrorTrip(trip || {})
  const data = t.data || {}
  const quoted = quotedTotal(t)
  const quotedCheck = checkQuotedTotal(data.finTotal)

  const clientName = tripClientName(t)
  const destination = tripDestination(t)
  const supplier = tripSupplier(t)

  return {
    id: text(t.id),
    schemaVersion: TRIP_SCHEMA_VERSION,
    summaryVersion: SUMMARY_VERSION,

    clientName,
    clientId: text(t.clientId),
    destination,
    supplier,
    dates: text(t.dates),
    preset: text(t.preset),
    status: text(t.status) || 'planning',

    checkin: tripCheckin(t),
    checkout: tripCheckout(t),
    depositDue: text(data.depositDue),
    finalDue: text(data.finalDue),
    dateBooked: text(data.dateBooked),

    archived: isArchivedTrip(t),
    cancelled: isCancelled(t),
    booked: isBooked(t),
    pipelineStage: pipelineStage(t),

    created: Number.isFinite(t.created) ? Number(t.created) : 0,
    updatedAt: text(t.updatedAt),

    revenue: tripRevenue(t),
    quoted,
    revenueSource: revenueSource(t),
    /* True when `finTotal` holds something that is not one number — a budget
       band, a "TBD - 5000". The figure in `quoted` is then the first number
       in it and must not be shown as revenue. */
    quotedSuspect: text(data.finTotal) !== '' && !quotedCheck.ok,

    collected: tripPaid(t),
    scheduled: tripScheduled(t),
    outstanding: tripOutstanding(t),

    commission: tripCommission(t),
    commissionReceived: receivedCommission(t),
    commPaidMonth: commissionPaidMonth(t),

    /* Resolved the same way `clients/client-trips.js#tripContact` resolves
       them, so a summary and a whole trip hand the backfill the same contact. */
    email: text(data.clientEmail) || text(data.email),
    phone: text(data.clientPhone) || text(data.phone),

    pageCount: Array.isArray(t.pages) ? t.pages.length : 0,
    fieldCount: Object.keys(data).length,

    /* Pre-lowercased so a keystroke in the search box does not lowercase
       every trip's name again on every render. Legacy searches name and
       destination (app.html:5828); supplier is added because "Royal
       Caribbean" is how an advisor thinks of half their trips. */
    search: `${clientName} ${destination} ${supplier}`.toLowerCase().trim(),
  }
}

/**
 * Does this summary match what the advisor typed in the search box?
 * @param {any} summary @param {unknown} term @returns {boolean}
 */
export function matchesSearch(summary, term) {
  const needle = text(term).toLowerCase()
  if (!needle) return true
  return String((summary && summary.search) || '').includes(needle)
}

/**
 * Are two summaries identical? Used to skip an index write that would change
 * nothing — one edited trip should cost one write, not two.
 * @param {any} a @param {any} b @returns {boolean}
 */
export function summaryEquals(a, b) {
  if (!a || !b) return a === b
  const keys = Object.keys(tripSummary({}))
  return keys.every((key) => a[key] === b[key])
}

/**
 * Totals across many summaries, in cents underneath.
 *
 * The individual figures are dollars, matching every other public function in
 * `lib/`. Summing dollars as floats is what makes ten line items of 1234.56
 * add up to 12345.599999999997, so this hands the work to `money.js#sumMoney`
 * rather than using `reduce((s, x) => s + x)`.
 *
 * Cancelled trips count for nothing, exactly as `revenue.js#revenueTotals`
 * treats them. Archived ones DO count: an advisor's all-time revenue should
 * not shrink because they tidied their board.
 *
 * @param {any[]|null|undefined} summaries
 * @param {{includeCancelled?: boolean}} [options]
 * @returns {{trips: number, active: number, booked: number, archived: number, revenue: number, collected: number, scheduled: number, outstanding: number, commission: number, commissionReceived: number}}
 */
export function summaryTotals(summaries, options = {}) {
  const all = Array.isArray(summaries) ? summaries.filter(Boolean) : []
  const list = options.includeCancelled ? all : all.filter((s) => !s.cancelled)
  return {
    trips: all.length,
    active: all.filter((s) => !s.archived && !s.cancelled).length,
    booked: list.filter((s) => s.booked).length,
    archived: all.filter((s) => s.archived).length,
    revenue: sumMoney(list.map((s) => s.revenue)),
    collected: sumMoney(list.map((s) => s.collected)),
    scheduled: sumMoney(list.map((s) => s.scheduled)),
    outstanding: sumMoney(list.map((s) => s.outstanding)),
    commission: sumMoney(list.map((s) => s.commission)),
    commissionReceived: sumMoney(list.map((s) => s.commissionReceived)),
  }
}

/**
 * Roughly how many bytes an index of these summaries occupies. Rough on
 * purpose: it exists to keep the index away from Firestore's 1 MiB document
 * limit, and a JSON length is within a few percent of the encoded size for
 * flat string-and-number maps.
 *
 * @param {Record<string, any>|null|undefined} byId
 * @returns {number}
 */
export function estimateIndexBytes(byId) {
  if (!byId || typeof byId !== 'object') return 0
  try {
    const keys = Object.keys(byId)
    return keys.length ? JSON.stringify(byId).length : 0
  } catch {
    /* A cycle. Nothing that reaches here belongs in Firestore anyway, and
       throwing from inside a write path would be worse than saying 0. */
    return 0
  }
}
